// src/context/GlobalContext.js
import React, { createContext, ReactNode, useEffect, useState } from 'react';
import useAuth, { UserData } from '../hooks/auth';
import { GetTestAccounts } from '../hooks/firebase';
import firestore from '../firestore';
import { TEMP_DISABLE_FULL_ONLINE_SALES, TEMP_ENABLE_FULL_ONLINE_SALES } from '../utils/constants';

export const PROD_TYPES = ['phone', 'iphone', 'tablet', 'laptop', 'other', 'loan'] as const;

export type StoreOffer = {
	id: string;
	amount_financed: number;
	brand: string;
	device_commercial_name: string;
	color: string | null;
	is_active: boolean;
	starts_at: { seconds: number; nanoseconds: number };
	expires_at: { seconds: number; nanoseconds: number };
	loan_duration: number;
	locked_price: number;
	store_email: string;
	master_store_email: string;
	payments_amount: number;
	ram: number;
	storage: number;
	user_lat: number;
	user_lng: number;
	address: string;
	delivery_method: 'pickup' | 'delivery';
	delivery_type: 'FAST' | 'STANDARD' | null;
};

const GlobalContext = createContext<{
	authenticated: boolean;
	userData: UserData | null;
	shouldLogin: boolean;
	internal: boolean;
	accessToOtherStores: any;
	newVersionAvailable: boolean;
	isAdminAccount: boolean;
	internalPath: { get: () => string; set: (path: string) => void };
	internalCountry: { get: () => string; set: (country: string) => void };
	testAccounts: string[];
	CountrySettings: CountrySettings | null;
	LevelsConfig: { venezuela: LevelsConfig; peru: LevelsConfig; rd: LevelsConfig } | null;
	storeAgentAuth: string | null;
	storeOffers: StoreOffer[];
	apkUrl: string | null;
}>({
	authenticated: false,
	userData: null,
	shouldLogin: false,
	internal: false,
	accessToOtherStores: null,
	newVersionAvailable: false,
	isAdminAccount: false,
	internalPath: { get: () => '', set: () => {} },
	internalCountry: { get: () => '', set: () => {} },
	testAccounts: [],
	CountrySettings: null,
	LevelsConfig: null,
	storeAgentAuth: null,
	storeOffers: [],
	apkUrl: null
});

function GlobalProvider({ children }: { children: ReactNode }) {
	const {
		authenticated,
		userData,
		shouldLogin,
		internal,
		accessToOtherStores,
		newVersionAvailable,
		appSettingsApkUrl,
		isAdminAccount
	} = useAuth();
	const [internalPath, setInternalPath] = useState('');
	const [internalCountry, setInternalCountry] = useState('');
	const testAccounts = GetTestAccounts();
	const [CountrySettings, setCountrySettings] = useState<CountrySettings | null>(null);
	const [storeAgentAuth, setStoreAgentAuth] = useState<string | null>(null);
	const [storeOffers, setStoreOffers] = useState<StoreOffer[]>([]);

	// Set internal country to be equal to userData.country if it's not set
	useEffect(() => {
		if (!userData && internalCountry) {
			setInternalCountry('');
			return;
		}

		if (internalCountry) return;

		let country = userData?.country;
		if (userData?.access_countries && userData?.access_countries.length === 1) {
			country = userData.access_countries[0];
		}

		if (country) {
			setInternalCountry(country);
		}
	}, [internalCountry, userData]);

	useEffect(() => {
		if (!userData?.country && !internalCountry) {
			setCountrySettings(null);
			return;
		}

		const unsubscribe = firestore
			.collection('CountrySettings')
			.doc(internalCountry || userData?.country)
			.onSnapshot(async (doc) => {
				try {
					if (doc?.exists && doc?.data()) {
						console.log('Set country settings for', (internalCountry || userData?.country) as string);
						setCountrySettings(doc.data() as CountrySettings);
					} else {
						setCountrySettings(null);
					}
				} catch (error) {
					console.error('Error getting country settings:', error);
				}
			});

		return () => unsubscribe();
	}, [userData, internalCountry]);

	// CountrySettings.apk_url takes priority over AppSettings.apk_url
	const apkUrl = CountrySettings?.apk_url || appSettingsApkUrl;

	const [LevelsConfig, setLevelsConfig] = useState<{ venezuela: LevelsConfig; peru: LevelsConfig; rd: LevelsConfig } | null>(
		null
	);
	useEffect(() => {
		const unsubscribe = firestore.collection('Levels').onSnapshot(async (docs) => {
			try {
				if (docs.docs.length > 0) {
					const config = {} as any;
					for (const doc of docs.docs) {
						if (doc.exists) {
							config[doc.id] = doc.data() as any;
						}
					}
					console.log('Set levels config');
					setLevelsConfig(config);
				} else {
					setLevelsConfig(null);
				}
			} catch (error) {
				console.error('Error getting levels config:', error);
			}
		});

		return () => unsubscribe();
	}, []);

	// Subscribe to StoreAgentAuth document for the logged-in store
	useEffect(() => {
		if (!userData?.email) {
			setStoreAgentAuth(null);
			return;
		}

		const unsubscribe = firestore
			.collection('StoreAgentAuth')
			.doc(userData.email)
			.onSnapshot((doc) => {
				try {
					const data = doc?.exists ? doc.data() : null;
					if (data?.show_dialog === true && data?.auth_code) {
						setStoreAgentAuth(String(data.auth_code));
					} else {
						setStoreAgentAuth(null);
					}
				} catch (error) {
					console.error('Error getting store agent auth:', error);
				}
			});

		return () => unsubscribe();
	}, [userData?.email]);

	// Subscribe to StoreOffers for the logged-in store (gated by online_sales_access)
	useEffect(() => {
		if ((TEMP_DISABLE_FULL_ONLINE_SALES || !userData?.email || (!userData?.master_store?.online_sales_access && !TEMP_ENABLE_FULL_ONLINE_SALES))) {
			setStoreOffers([]);
			return;
		}

		const unsubscribe = firestore
			.collection('StoreOffers')
			.where('master_store_email', '==', userData.master_store.email)
			.onSnapshot((snapshot) => {
				try {
					const now = Date.now();
					const offers = snapshot.docs
						.map((doc) => ({ ...doc.data(), id: doc.id }) as StoreOffer)
						.filter((offer) => {
							if (!offer.is_active) return false;
							if (offer.store_email !== userData.email && !userData.master_user) return false;
							const startsMs = offer.starts_at.seconds * 1000;
							const expiresMs = offer.expires_at.seconds * 1000;
							return now >= startsMs && now < expiresMs;
						});
					setStoreOffers(offers);
				} catch (error) {
					console.error('Error getting store offers:', error);
				}
			});

		return () => unsubscribe();
	}, [userData?.email, userData?.master_store?.online_sales_access]);

	return (
		<GlobalContext.Provider
			value={{
				authenticated,
				userData,
				shouldLogin,
				internal,
				accessToOtherStores,
				newVersionAvailable,
				isAdminAccount,
				internalPath: { get: () => internalPath, set: (path: string) => setInternalPath(path) },
				internalCountry: { get: () => internalCountry, set: (country: string) => setInternalCountry(country) },
				testAccounts,
				CountrySettings,
				LevelsConfig,
				storeAgentAuth,
				storeOffers,
				apkUrl
			}}
		>
			{children}
		</GlobalContext.Provider>
	);
}

export { GlobalContext, GlobalProvider };

export type WebBanner = {
	id?: string;
	image_url?: string;
	title?: string;
	description?: string;
	redirect_url?: string;
	button_text?: string;
	is_url_primary?: boolean;
	is_active?: boolean;
};

export type CountrySettings = {
	apk_url?: string;
	level_1_min_down_payment_pct_promo_block_threshold?: number;
	biometric_available_methods?: {
		liveness_detection?: boolean;
	};
	uid_prefix?: string;
	instructions?: { title: string; subtitle?: string; description?: string; thumbnail?: string; url: string }[];
	announcements?: {
		active_promos?: Array<{
			title?: string;
			subtitle?: string;
			description?: string;
			thumbnail?: string;
			download_url?: string;
		}>;
		upcoming_promos?: Array<{
			title?: string;
			subtitle?: string;
			description?: string;
			thumbnail?: string;
			download_url?: string;
		}>;
		marketing_material?: Array<{
			title?: string;
			subtitle?: string;
			description?: string;
			thumbnail?: string;
			download_url?: string;
		}>;
	};
	whatsapp_link?: string;
	ata_numbers?: Array<string>;
	tyc_link?: string;
	down_payment?: {
		level_1?: number;
		level_3?: number;
		level_2?: number;
		level_4?: number;
	};
	id_name?: string;
	safe_stores?: Array<string>;
	cantvRESmin?: number;
	skip_face_verification?: boolean;
	country_name?: string;
	interMin?: number;
	country_managers?: Array<string>;
	movilnetMin?: number;
	id_types?: Array<{
		code?: string;
		name?: string;
		country?: string;
	}>;
	customer_care_number?: string;
	privacy_link?: string;
	legal?: {
		self_financed_terms?: string;
		financed_by_krece_terms?: string;
	};
	interest_rate_changes?: {
		device_price?: {
			'0-100'?: number;
			'201-300'?: number;
			'500+'?: number;
			'101-130'?: number;
			'131-200'?: number;
			'300-500'?: number;
		};
		levels?: {
			level_4?: number;
			level_3?: number;
			level_2?: number;
		};
		city?: {
			maracaibo?: number;
			caucagua?: number;
			caracas?: number;
			margarita?: number;
			maracay?: number;
		};
		age?: {
			'40-50'?: number;
			'50-60'?: number;
			'0-25'?: number;
			'60+'?: number;
			'25-40'?: number;
		};
	};
	last_bnc_refresh?: {
		seconds?: number;
		nanoseconds?: number;
	};
	facebook_url?: string;
	terms_link?: string;
	payment_options?: {
		level_1?: Array<{
			installments?: number;
			factor?: number;
		}>;
	};
	web_banner?: WebBanner;
	web_banners?: WebBanner[];
	brands_not_allowed?: Array<string>;
	ata_shared_email?: string;
	movistarMin?: number;
	contact_email?: string;
	cookies_link?: string;
	cantvTVmin?: number;
	currency_code?: string;
	terms_link_auto?: string;
	price_ranges?: {
		laptop?: {
			min?: number;
			max?: number;
		};
		others?: {
			max?: number;
			min?: number;
		};
		phone?: {
			max?: number;
			min?: number;
		};
		iphone?: {
			min?: number;
			max?: number;
		};
	};
	cities?: Array<string>;
	payment_discount?: number;
	max_cuota_payment?: number;
	store_payment_2fa_always_required_threshold?: number;
	currency_name?: string;
	price_max?: number;
	digital_invoice_numbers?: string;
	max_payment_allowed?: string;
	phone_number_extension?: string;
	feature_flags?: {
		disable_sale_phone_2fa?: boolean;
		cobranza_portal?: boolean;
		promotions_portal?: boolean;
		intercom_portal?: boolean;
	};
	referrals?: {
		amount?: number;
		message?: string;
		limit?: number;
		active?: boolean;
		type?: string;
		image_url?: string;
	};
	salary_ranges?: Array<string>;
	digitelMin?: number;
	instagram_url?: string;
	last_bnc_batch_number?: number;
	price_min?: number;
	twitter_url?: string;
	anexo_link?: string;
	country_flag?: string;
	price_control?: boolean;
	phone_number?: string;
	faq_link?: string;
	banks?: Array<string>;
	delinquency_ranges?: {
		low?: number;
		high?: number;
	};
	services_config?: {
		personal_loans?: {
			required_points?: number;
		};
		recargas?: {
			is_active?: boolean;
			one_time_activation_fee?: number;
			operators?: {
				movistar?: {
					min: number;
					max: number;
					step: number;
					new_user_max?: number;
				};
				digitel?: {
					min: number;
					max: number;
					step: number;
					new_user_max?: number;
				};
				movilnet?: {
					min: number;
					max: number;
					step: number;
					new_user_max?: number;
				};
				altice?: {
					min: number;
					max: number;
					step: number;
					new_user_max?: number;
				};
				claro?: {
					min: number;
					max: number;
					step: number;
					new_user_max?: number;
				};
				viva?: {
					min: number;
					max: number;
					step: number;
					new_user_max?: number;
				};
			};
		};
	};
};

export type FinancingOptions = {
	product_types?: typeof PROD_TYPES;
	levels?: ('level_1' | 'level_2' | 'level_3' | 'level_4')[];
	down_payment?: {
		granularity?: number;
		max?: number;
		min?: number;
	};
	payment_options?: {
		factor?: number;
		installments?: number;
	}[];
};
export type LevelConfig = {
	information: {
		name: string;
		color: string;
		benefits: string[];
		points_range: {
			max?: number;
			min: number;
		};
	};
	credit_line?: number;
	credit_line_secondary?: number;
	recarga_fee?: number;
	additional_financing_options?: FinancingOptions[];
	additional_price_ranges?: {
		laptop: {
			min: number;
			max: number;
		};
		other: {
			max: number;
			min: number;
		};
		phone: {
			max: number;
			min: number;
		};
		iphone: {
			min: number;
			max: number;
		};
		tablet: {
			min: number;
			max: number;
		};
		loan: {
			min: number;
			max: number;
		};
	};
	personal_loan?: {
		max?: number;
		min?: number;
	};
};

export type LevelsConfig = {
	base_price_ranges: {
		laptop: {
			min: number;
			max: number;
			credit_line?: 'primary' | 'secondary';
		};
		other: {
			max: number;
			min: number;
			credit_line?: 'primary' | 'secondary';
		};
		phone: {
			max: number;
			min: number;
			credit_line?: 'primary' | 'secondary';
		};
		iphone: {
			min: number;
			max: number;
			credit_line?: 'primary' | 'secondary';
		};
		tablet: {
			min: number;
			max: number;
			credit_line?: 'primary' | 'secondary';
		};
		loan: {
			min: number;
			max: number;
			credit_line?: 'primary' | 'secondary';
		};
	};
	base_financing_options: Required<FinancingOptions>[];
	level_1?: LevelConfig;
	level_2?: LevelConfig;
	level_3?: LevelConfig;
	level_4?: LevelConfig;
};
